"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { SourceLogo, type SourceId } from "./SourceLogos";

export interface SourceItem {
  id: SourceId;
  /** Tooltip text and accessible name, e.g. "YouTube". */
  name: string;
}

interface SourceLogoListProps {
  items: SourceItem[];
  /** Accessible name of the list (or use `labelledBy`). */
  label?: string;
  /** Id of a visible element that names the list. */
  labelledBy?: string;
  /** Glyph size (px) at ≥769 and <769. */
  glyph: number;
  glyphMobile?: number;
  /** Padding around the glyph (px) at ≥769 and <769: hit target = glyph + 2 × pad. */
  pad: number;
  padMobile?: number;
  /** Gap between hit targets (px) at ≥769 and <769. */
  gap: number;
  gapMobile?: number;
  /** framed: white tile with a hairline border; none: the logo alone. */
  surface?: "framed" | "none";
  tooltipSide?: "top" | "bottom";
  className?: string;
}

const TOUCH_HIDE_MS = 2500;

/**
 * One row of logo-only source marks. Each mark is a button named by
 * aria-label; a visible tooltip with the same text (aria-hidden) opens on
 * mouse hover, keyboard focus (:focus-visible only) and tap. Escape, a tap
 * outside, or 2.5 s after a tap close it. Tapping never navigates.
 */
export function SourceLogoList({
  items,
  label,
  labelledBy,
  glyph,
  glyphMobile = glyph,
  pad,
  padMobile = pad,
  gap,
  gapMobile = gap,
  surface = "none",
  tooltipSide = "bottom",
  className = "",
}: SourceLogoListProps) {
  const [active, setActive] = useState<SourceId | null>(null);
  const [openedByTouch, setOpenedByTouch] = useState(false);
  const lastPointerType = useRef("mouse");
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!active) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!listRef.current?.contains(e.target as Node)) setActive(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActive(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    const timer = openedByTouch ? window.setTimeout(() => setActive(null), TOUCH_HIDE_MS) : undefined;
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      if (timer) window.clearTimeout(timer);
    };
  }, [active, openedByTouch]);

  const close = (id: SourceId) => setActive((current) => (current === id ? null : current));

  // Desktop/mobile sizes as CSS variables; `max-tablet:` swaps them (<769 px).
  const vars = {
    "--sl-gd": `${glyph}px`,
    "--sl-gm": `${glyphMobile}px`,
    "--sl-pd": `${pad}px`,
    "--sl-pm": `${padMobile}px`,
    "--sl-gapd": `${gap}px`,
    "--sl-gapm": `${gapMobile}px`,
    gap: "var(--sl-gap)",
  } as CSSProperties;
  const target = "calc(var(--sl-g) + 2 * var(--sl-p))";
  const surfaceClass =
    surface === "framed"
      ? "bg-white border border-black/[0.08] transition-[border-color] duration-150 hover:border-black/[0.18] data-[open=true]:border-black/[0.18] motion-reduce:transition-none"
      : "bg-transparent border-0";

  return (
    <ul
      ref={listRef}
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className={`m-0 flex list-none flex-nowrap items-center p-0 [--sl-g:var(--sl-gd)] [--sl-p:var(--sl-pd)] [--sl-gap:var(--sl-gapd)] max-tablet:[--sl-g:var(--sl-gm)] max-tablet:[--sl-p:var(--sl-pm)] max-tablet:[--sl-gap:var(--sl-gapm)] ${className}`}
      style={vars}
    >
      {items.map((item) => {
        const open = active === item.id;
        return (
          <li
            key={item.id}
            className="relative flex shrink-0 items-center"
            onPointerEnter={(e) => {
              if (e.pointerType !== "mouse") return;
              setOpenedByTouch(false);
              setActive(item.id);
            }}
            onPointerLeave={(e) => {
              if (e.pointerType === "mouse") close(item.id);
            }}
          >
            <button
              type="button"
              aria-label={item.name}
              data-open={open}
              className={`flex cursor-default items-center justify-center p-0 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${surfaceClass}`}
              style={{ width: target, height: target, borderRadius: `calc(${target} * 0.25)`, WebkitTapHighlightColor: "transparent" }}
              onPointerDown={(e) => {
                lastPointerType.current = e.pointerType;
              }}
              onClick={(e) => {
                // detail === 0 is Enter/Space: focus already shows the tooltip.
                if (e.detail === 0 || lastPointerType.current === "mouse") return;
                setOpenedByTouch(true);
                setActive((current) => (current === item.id ? null : item.id));
              }}
              onFocus={(e) => {
                if (!e.currentTarget.matches(":focus-visible")) return;
                setOpenedByTouch(false);
                setActive(item.id);
              }}
              onBlur={() => close(item.id)}
            >
              <span className="block" style={{ width: "var(--sl-g)", height: "var(--sl-g)" }}>
                <SourceLogo id={item.id} size="100%" />
              </span>
            </button>

            {/* Tooltip. The 6 px padding bridges the gap so it stays open while
                the pointer moves onto it (WCAG 1.4.13). */}
            <span
              aria-hidden="true"
              className={`absolute left-1/2 z-10 -translate-x-1/2 transition-[opacity,translate] duration-150 ease-out motion-reduce:transition-none ${
                tooltipSide === "bottom" ? "top-full pt-[6px]" : "bottom-full pb-[6px]"
              } ${
                open
                  ? "pointer-events-auto translate-y-0 opacity-100"
                  : `pointer-events-none opacity-0 motion-reduce:translate-y-0 ${tooltipSide === "bottom" ? "-translate-y-[2px]" : "translate-y-[2px]"}`
              }`}
            >
              <span className="block whitespace-nowrap rounded-[6px] bg-[#1A1D1F] px-[8px] py-[4px] text-[12px] font-medium leading-[16px] text-white shadow-md ring-1 ring-white/15 [font-family:var(--font-commissioner)]">
                {item.name}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
